<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

// Ordered list of requirement templates stored in config.requirements. Each requirement:
//   eligibility:  { key, kind:'eligibility', label, description, required, critical,
//                   match:'all'|'any', conditions:[{ attribute, operator, value }], messages:{ unmet } }
//   attestation:  { key, kind:'attestation', label, description, required, critical }
// Enforcement is advisory — these surface met/unmet status to agents and AI, they never hard-block.
const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  // Known applicant attribute keys offered in the picker (custom attrs + standard keys).
  attributeOptions: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const KINDS = ['eligibility', 'attestation'];
const OPERATORS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'truthy', 'falsy'];
const VALUELESS_OPERATORS = ['truthy', 'falsy'];
const LIST_OPERATORS = ['in'];

const requirements = computed({
  get: () => props.modelValue || [],
  set: value => emit('update:modelValue', value),
});

function updateRequirement(index, patch) {
  requirements.value = requirements.value.map((req, idx) =>
    idx === index ? { ...req, ...patch } : req
  );
}

function addRequirement() {
  requirements.value = [
    ...requirements.value,
    {
      key: '',
      kind: 'eligibility',
      label: '',
      description: '',
      required: true,
      critical: false,
      match: 'all',
      conditions: [],
      messages: {},
    },
  ];
}

function removeRequirement(index) {
  requirements.value = requirements.value.filter((_, idx) => idx !== index);
}

function moveRequirement(index, direction) {
  const target = index + direction;
  if (target < 0 || target >= requirements.value.length) return;
  const next = [...requirements.value];
  [next[index], next[target]] = [next[target], next[index]];
  requirements.value = next;
}

// --- Conditions (eligibility only) ---
function conditionsOf(req) {
  return Array.isArray(req.conditions) ? req.conditions : [];
}

function setConditions(index, conditions) {
  updateRequirement(index, { conditions });
}

function addCondition(index) {
  setConditions(index, [
    ...conditionsOf(requirements.value[index]),
    { attribute: '', operator: 'eq', value: '' },
  ]);
}

function removeCondition(reqIndex, condIndex) {
  setConditions(
    reqIndex,
    conditionsOf(requirements.value[reqIndex]).filter((_, idx) => idx !== condIndex)
  );
}

function patchCondition(reqIndex, condIndex, patch) {
  const conditions = conditionsOf(requirements.value[reqIndex]).map((cond, idx) =>
    idx === condIndex ? { ...cond, ...patch } : cond
  );
  setConditions(reqIndex, conditions);
}

// `in` takes a comma-separated list → array; other operators keep the scalar string.
function onValueInput(reqIndex, condIndex, operator, raw) {
  const value = LIST_OPERATORS.includes(operator)
    ? raw.split(',').map(part => part.trim()).filter(Boolean)
    : raw;
  patchCondition(reqIndex, condIndex, { value });
}

function valueDisplay(cond) {
  return Array.isArray(cond.value) ? cond.value.join(', ') : (cond.value ?? '');
}

function patchMessages(index, patch) {
  updateRequirement(index, {
    messages: { ...(requirements.value[index].messages || {}), ...patch },
  });
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <p v-if="!requirements.length" class="py-2 text-xs text-n-slate-10">
      {{ t('TRAMITE_TYPES.REQUIREMENTS.EMPTY') }}
    </p>

    <div
      v-for="(req, index) in requirements"
      :key="index"
      class="flex flex-col gap-3 p-3 rounded-lg border border-n-weak"
    >
      <!-- Header: kind + label + reorder + remove -->
      <div class="flex flex-wrap gap-2 items-center">
        <select
          :value="req.kind || 'eligibility'"
          class="px-1.5 h-8 text-sm rounded-md border w-36 border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
          @change="updateRequirement(index, { kind: $event.target.value })"
        >
          <option v-for="kind in KINDS" :key="kind" :value="kind">
            {{ t(`TRAMITE_TYPES.REQUIREMENTS.KINDS.${kind}`) }}
          </option>
        </select>
        <input
          :value="req.label"
          type="text"
          :placeholder="t('TRAMITE_TYPES.REQUIREMENTS.LABEL_PLACEHOLDER')"
          class="flex-1 px-2 h-8 text-sm font-medium rounded-md border min-w-40 border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
          @input="updateRequirement(index, { label: $event.target.value })"
        />
        <Button
          v-tooltip.top="t('TRAMITE_TYPES.REQUIREMENTS.MOVE_UP')"
          variant="ghost"
          color="slate"
          size="xs"
          icon="i-lucide-chevron-up"
          :disabled="index === 0"
          @click="moveRequirement(index, -1)"
        />
        <Button
          v-tooltip.top="t('TRAMITE_TYPES.REQUIREMENTS.MOVE_DOWN')"
          variant="ghost"
          color="slate"
          size="xs"
          icon="i-lucide-chevron-down"
          :disabled="index === requirements.length - 1"
          @click="moveRequirement(index, 1)"
        />
        <Button
          variant="ghost"
          color="ruby"
          size="xs"
          icon="i-lucide-trash-2"
          @click="removeRequirement(index)"
        />
      </div>

      <!-- Description (citizen-facing; for attestation it's the "declaro…" text) -->
      <input
        :value="req.description"
        type="text"
        :placeholder="t('TRAMITE_TYPES.REQUIREMENTS.DESCRIPTION_PLACEHOLDER')"
        class="px-2 h-8 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
        @input="updateRequirement(index, { description: $event.target.value })"
      />

      <!-- Flags -->
      <div class="flex gap-4 items-center text-xs text-n-slate-11">
        <label class="flex gap-1.5 items-center cursor-pointer">
          <input
            type="checkbox"
            class="rounded"
            :checked="req.required !== false"
            @change="updateRequirement(index, { required: $event.target.checked })"
          />
          {{ t('TRAMITE_TYPES.REQUIREMENTS.REQUIRED') }}
        </label>
        <label class="flex gap-1.5 items-center cursor-pointer">
          <input
            type="checkbox"
            class="rounded"
            :checked="!!req.critical"
            @change="updateRequirement(index, { critical: $event.target.checked })"
          />
          {{ t('TRAMITE_TYPES.REQUIREMENTS.CRITICAL') }}
        </label>
      </div>

      <!-- Conditions (eligibility only) -->
      <div v-if="(req.kind || 'eligibility') === 'eligibility'" class="flex flex-col gap-2 pl-1">
        <div class="flex gap-2 items-center text-xs text-n-slate-10">
          <span>{{ t('TRAMITE_TYPES.REQUIREMENTS.WHEN') }}</span>
          <select
            :value="req.match || 'all'"
            class="px-1.5 h-6 text-xs rounded border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
            @change="updateRequirement(index, { match: $event.target.value })"
          >
            <option value="all">{{ t('TRAMITE_TYPES.REQUIREMENTS.MATCH_ALL') }}</option>
            <option value="any">{{ t('TRAMITE_TYPES.REQUIREMENTS.MATCH_ANY') }}</option>
          </select>
        </div>

        <div
          v-for="(cond, condIndex) in conditionsOf(req)"
          :key="condIndex"
          class="flex flex-wrap gap-2 items-center"
        >
          <input
            :value="cond.attribute"
            list="tramite-requirement-attributes"
            type="text"
            :placeholder="t('TRAMITE_TYPES.REQUIREMENTS.ATTRIBUTE_PLACEHOLDER')"
            class="px-2 h-8 font-mono text-sm rounded-md border w-40 border-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
            @input="patchCondition(index, condIndex, { attribute: $event.target.value })"
          />
          <select
            :value="cond.operator || 'eq'"
            class="px-1.5 h-8 text-sm rounded-md border w-32 border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
            @change="patchCondition(index, condIndex, { operator: $event.target.value })"
          >
            <option v-for="op in OPERATORS" :key="op" :value="op">
              {{ t(`TRAMITE_TYPES.PAYMENT.OPERATORS.${op}`) }}
            </option>
          </select>
          <input
            v-if="!VALUELESS_OPERATORS.includes(cond.operator)"
            :value="valueDisplay(cond)"
            type="text"
            :placeholder="
              LIST_OPERATORS.includes(cond.operator)
                ? t('TRAMITE_TYPES.REQUIREMENTS.VALUE_LIST_PLACEHOLDER')
                : t('TRAMITE_TYPES.REQUIREMENTS.VALUE_PLACEHOLDER')
            "
            class="flex-1 px-2 h-8 text-sm rounded-md border min-w-24 border-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
            @input="onValueInput(index, condIndex, cond.operator, $event.target.value)"
          />
          <Button
            variant="ghost"
            color="ruby"
            size="xs"
            icon="i-lucide-x"
            @click="removeCondition(index, condIndex)"
          />
        </div>

        <div>
          <Button
            type="button"
            variant="ghost"
            color="slate"
            size="xs"
            icon="i-lucide-plus"
            :label="t('TRAMITE_TYPES.REQUIREMENTS.ADD_CONDITION')"
            @click="addCondition(index)"
          />
        </div>

        <!-- Optional citizen-facing message shown when unmet -->
        <input
          :value="req.messages?.unmet ?? ''"
          type="text"
          :placeholder="t('TRAMITE_TYPES.REQUIREMENTS.UNMET_MESSAGE_PLACEHOLDER')"
          class="px-2 h-8 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
          @input="patchMessages(index, { unmet: $event.target.value })"
        />
      </div>
    </div>

    <datalist id="tramite-requirement-attributes">
      <option v-for="opt in attributeOptions" :key="opt" :value="opt" />
    </datalist>

    <div>
      <Button
        type="button"
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-plus"
        :label="t('TRAMITE_TYPES.REQUIREMENTS.ADD')"
        @click="addRequirement"
      />
    </div>
  </div>
</template>
